const IST_TIME_ZONE = "Asia/Kolkata";
const IST_OFFSET_MINUTES = 330;

const getDateOnlyParts = (value) => {
    const isoDate = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoDate) {
        return {
            year: Number(isoDate[1]),
            month: Number(isoDate[2]),
            day: Number(isoDate[3])
        };
    }

    const displayDate = String(value).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (displayDate) {
        return {
            year: Number(displayDate[3]),
            month: Number(displayDate[2]),
            day: Number(displayDate[1])
        };
    }

    return null;
};

export const getISTDateParts = (value = new Date()) => {
    const parts = new Intl.DateTimeFormat("en-IN", {
        timeZone: IST_TIME_ZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
    }).formatToParts(value instanceof Date ? value : new Date(value));

    return Object.fromEntries(parts.map(({ type, value }) => [type, value]));
};

export const getISTTimeParts = (value = new Date()) => {
    const parts = new Intl.DateTimeFormat("en-IN", {
        timeZone: IST_TIME_ZONE,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23"
    }).formatToParts(value instanceof Date ? value : new Date(value));

    return Object.fromEntries(parts.map(({ type, value }) => [type, value]));
};

export const getISTDateInputValue = (value = new Date()) => {
    const { year, month, day } = getISTDateParts(value);
    return `${year}-${month}-${day}`;
};

export const getISTCalendarDate = (value = new Date()) => {
    const parts = getISTDateParts(value);
    return new Date(Number(parts.year), Number(parts.month) - 1, Number(parts.day));
};

export const getISTDateOnlyTimestamp = (value) => {
    const dateOnly = getDateOnlyParts(value);
    if (dateOnly) {
        return Date.UTC(dateOnly.year, dateOnly.month - 1, dateOnly.day);
    }

    const timestamp = parseISTTimestamp(value);
    if (!Number.isFinite(timestamp)) return null;

    const parts = getISTDateParts(new Date(timestamp));
    return Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day));
};

export const parseISTTimestamp = (value) => {
    if (value instanceof Date) return value.getTime();
    if (value === null || value === undefined || value === "") return NaN;

    const text = String(value).trim();
    const dateOnly = getDateOnlyParts(text);
    if (dateOnly) {
        return Date.UTC(dateOnly.year, dateOnly.month - 1, dateOnly.day);
    }

    const localDateTime = text.match(
        /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?$/
    );
    if (localDateTime) {
        const [, year, month, day, hour, minute, second = "0", fraction = "0"] = localDateTime;
        const milliseconds = Number(`0.${fraction}`) * 1000;
        return Date.UTC(
            Number(year),
            Number(month) - 1,
            Number(day),
            Number(hour),
            Number(minute),
            Number(second),
            milliseconds
        ) - IST_OFFSET_MINUTES * 60 * 1000;
    }

    return Date.parse(text);
};

export const formatISTDate = (value, options = {}) => {
    if (value === null || value === undefined || value === "") return "-";

    const dateOnly = getDateOnlyParts(value);
    const timestamp = dateOnly
        ? Date.UTC(dateOnly.year, dateOnly.month - 1, dateOnly.day)
        : parseISTTimestamp(value);
    if (!Number.isFinite(timestamp)) return "-";

    const hasDateOptions = ["day", "month", "year", "weekday", "dateStyle"].some(
        (key) => key in options
    );

    return new Intl.DateTimeFormat("en-IN", {
        ...(hasDateOptions ? {} : {
            day: "2-digit",
            month: "short",
            year: "numeric"
        }),
        ...options,
        timeZone: dateOnly ? "UTC" : IST_TIME_ZONE
    }).format(new Date(timestamp));
};

export const formatISTDateTime = (value, options = {}) => {
    if (value === null || value === undefined || value === "") return "-";

    const dateOnly = getDateOnlyParts(value);
    const timestamp = dateOnly
        ? Date.UTC(dateOnly.year, dateOnly.month - 1, dateOnly.day)
        : parseISTTimestamp(value);
    if (!Number.isFinite(timestamp)) return "-";

    const formatterOptions = dateOnly
        ? Object.fromEntries(
            Object.entries(options).filter(([key]) =>
                !["timeStyle", "hour", "minute", "second", "hour12", "dayPeriod"].includes(key)
            )
        )
        : options;

    const hasDateOptions = ["day", "month", "year", "weekday", "dateStyle"].some(
        (key) => key in formatterOptions
    );
    const hasTimeOptions = ["hour", "minute", "second", "timeStyle"].some(
        (key) => key in formatterOptions
    );

    return new Intl.DateTimeFormat("en-IN", {
        ...(dateOnly
            ? (hasDateOptions ? {} : { dateStyle: "short" })
            : (hasDateOptions || hasTimeOptions
                ? {}
                : { dateStyle: "short", timeStyle: "short" })),
        ...formatterOptions,
        timeZone: dateOnly ? "UTC" : IST_TIME_ZONE
    }).format(new Date(timestamp));
};

export const formatISTTime = (value, options = {}) => {
    const timestamp = parseISTTimestamp(value);
    if (!Number.isFinite(timestamp)) return "-";

    return new Intl.DateTimeFormat("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        ...options,
        timeZone: IST_TIME_ZONE
    }).format(new Date(timestamp));
};
